import { ImageBackground, Pressable, View } from 'react-native'
import React, { useEffect } from 'react'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { useDispatch, useSelector } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StyleService, useStyleSheet } from '@style/service.ts'
import { BenefitCard } from '@screens/Marketplace/components/NewMarketplace/ProductExplanation/BenefitCard.tsx'
import { OnboardingStackParamsList } from '@navigation/types'
import { videoCallProductSelector } from '@selectors/app.ts'
import {
  getProductExplanationScreenDefinition,
  ProductExplanationScreenTypes,
  VideoCallProductTypes,
} from '@screens/Marketplace/components/NewMarketplace/definitions.tsx'
import { BillingProductCategory } from '@src/types'
import { LoadingContainer } from '@src/screens/Common/containers/LoadingContainer'
import { LinearGradient } from '@src/components'
import { WhiteText } from '@src/screens/Marketplace/components/NewMarketplace/WhiteText'

const TAP_ANYWHERE_LABEL = 'Tap anywhere to continue'

const BACKGROUND_COLOR = '#181818'

export const PaidVideoCallsProductExplanation = () => {
  const styles = useStyleSheet(themedStyle)
  const navigation = useNavigation<StackNavigationProp<OnboardingStackParamsList>>()
  const insets = useSafeAreaInsets()
  const dispatch = useDispatch()
  const route = useRoute<RouteProp<OnboardingStackParamsList, 'PaidVideoCallsProductExplanation'>>()
  const { meetingAt, coachId } = route.params || {}

  useEffect(() => {
    dispatch({
      type: 'app/fetchProducts',
      payload: { category: BillingProductCategory.NutritionistHub },
    })
  }, [dispatch])

  const paidVideoCallProduct = useSelector(videoCallProductSelector)

  if (!paidVideoCallProduct) {
    return <LoadingContainer />
  }

  const definition = getProductExplanationScreenDefinition(
    VideoCallProductTypes.Paid,
    paidVideoCallProduct,
  )[ProductExplanationScreenTypes.PaidVideoCalls]

  const onPress = () => {
    navigation.replace('VideoCallCheckout', { meetingAt, coachId })
  }

  const { description, backgroundImage, benefits } = definition

  return (
    <Pressable
      accessibilityLabel={TAP_ANYWHERE_LABEL}
      style={styles.imageContainer}
      onPress={onPress}
    >
      <ImageBackground source={backgroundImage.imageSource} style={styles.imageBackground}>
        <View style={[styles.topLayer, { backgroundColor: BACKGROUND_COLOR }]} />
        <LinearGradient
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          startColor={BACKGROUND_COLOR}
          startOpacity={1}
          endColor={BACKGROUND_COLOR}
          endOpacity={0}
          style={styles.topLayerGradient}
        />
        <View style={[styles.layer, { backgroundColor: BACKGROUND_COLOR, opacity: 0.7 }]} />

        <View style={styles.content}>
          <View style={{ marginTop: 22 + insets.top }}>
            <WhiteText type="large">
              Unfortunately, we do not accept your insurance at this time, but don’t worry...
            </WhiteText>
            <WhiteText type="title-1" style={styles.title}>
              You can get expert{' '}
              <WhiteText
                type="title-1"
                style={[styles.title, { fontStyle: 'italic', fontWeight: 700 }]}
              >
                nutritionist support
              </WhiteText>
              {'\n'}
              and pay as you go.
            </WhiteText>
            <WhiteText type="large" style={styles.description}>
              {description}
            </WhiteText>
          </View>

          <View>
            {benefits.map((benefit) => {
              return <BenefitCard key={benefit.title} benefits={benefits} benefit={benefit} />
            })}

            <View
              style={[styles.bottomLabelContainer, { paddingBottom: Math.max(insets.bottom, 32) }]}
            >
              <WhiteText type="large" bold>
                {TAP_ANYWHERE_LABEL}
              </WhiteText>
            </View>
          </View>
        </View>
      </ImageBackground>
    </Pressable>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
  },
  content: {
    justifyContent: 'space-between',
    marginHorizontal: 16,
    flex: 1,
  },
  title: {
    marginTop: 48,
    fontSize: 29,
    lineHeight: 31,
    fontWeight: 400,
  },
  description: {
    marginTop: 24,
  },
  imageContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageBackground: {
    flex: 1,
    width: '100%',
  },
  bottomLabelContainer: {
    alignItems: 'center',
    marginTop: 24,
  },
  layer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  topLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: 217,
  },
  topLayerGradient: {
    position: 'absolute',
    top: 217,
    left: 0,
    width: '100%',
    height: 88,
  },
})
